/**
 * Encode a canvas to base64 JPEG inside a size budget.
 *
 * Strategy:
 *   1. Walk quality 0.85 → 0.45 at the current canvas size; return the
 *      highest quality whose output is ≤ targetKB ("ideal" budget).
 *   2. If even the lowest-quality encoding exceeds maxKB, shrink the
 *      canvas by 15% and repeat — until the longest side hits MIN_DIM.
 *   3. If the lowest quality fits ≤ maxKB but no quality fits ≤ targetKB,
 *      return that lowest-quality encoding (acceptable fallback).
 *
 * Defaults target ~50 KB with a 150 KB hard ceiling — small enough for
 * cheap storage / LLM upload, while keeping handwriting legible.
 *
 * Returns the raw base64 string (no `data:image/...;base64,` prefix).
 */

const QUALITIES = [0.85, 0.75, 0.65, 0.55, 0.45];
const MIN_DIM = 600;

export interface EncodeBudgetOptions {
    targetKB?: number;
    maxKB?: number;
}

export function encodeCanvasToJpegBudget(
    source: HTMLCanvasElement,
    opts: EncodeBudgetOptions = {},
): string {
    const targetKB = opts.targetKB ?? 50;
    const maxKB = opts.maxKB ?? 150;

    let canvas = source;
    let lastUrl = '';
    let lastKB = Infinity;

    for (let pass = 0; pass < 6; pass++) {
        for (const q of QUALITIES) {
            const url = canvas.toDataURL('image/jpeg', q);
            const kb = (url.length * 0.75) / 1024;
            lastUrl = url;
            lastKB = kb;
            if (kb <= targetKB) return strip(url);
        }
        if (lastKB <= maxKB) return strip(lastUrl);

        const longest = Math.max(canvas.width, canvas.height);
        if (longest <= MIN_DIM) return strip(lastUrl);
        const scale = Math.max(MIN_DIM / longest, 0.85);
        canvas = drawScaled(canvas, scale);
    }
    return strip(lastUrl);
}

function drawScaled(src: HTMLCanvasElement, scale: number): HTMLCanvasElement {
    const w = Math.max(1, Math.round(src.width * scale));
    const h = Math.max(1, Math.round(src.height * scale));
    const next = document.createElement('canvas');
    next.width = w;
    next.height = h;
    next.getContext('2d')!.drawImage(src, 0, 0, w, h);
    return next;
}

function strip(dataUrl: string): string {
    return dataUrl.replace(/^data:image\/\w+;base64,/, '');
}

/**
 * Resize an image File to `maxSize` on its longest side and encode it under the
 * JPEG budget. Returns base64 with no `data:` prefix.
 *
 * Exists because every upload path needs the same three steps (read → resize →
 * budget-encode) and each one had written its own. The desktop bulk panel had
 * not: it uploaded the raw file, which is how 8 MB phone photos reached storage
 * and the analyzer — 415 blobs over 1 MB, 78% of the container, 259 of them in
 * one month. See AutoUploadPanel.
 *
 * Non-images are rejected by the caller; this assumes an image File.
 */
export function encodeImageFileToJpegBudget(
    file: File,
    maxSize = 1600,
    opts: EncodeBudgetOptions = {},
): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const img = new Image();
            img.onload = () => {
                let w = img.width;
                let h = img.height;
                if (w > maxSize || h > maxSize) {
                    const scale = maxSize / Math.max(w, h);
                    w = Math.round(w * scale);
                    h = Math.round(h * scale);
                }
                const canvas = document.createElement('canvas');
                canvas.width = w;
                canvas.height = h;
                const ctx = canvas.getContext('2d');
                if (!ctx) { reject(new Error('canvas 2d context unavailable')); return; }
                ctx.drawImage(img, 0, 0, w, h);
                resolve(encodeCanvasToJpegBudget(canvas, opts));
            };
            img.onerror = reject;
            img.src = reader.result as string;
        };
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
    });
}
